import { View, Text, TouchableOpacity } from "react-native";
import { Ionicons } from "@expo/vector-icons";
import { useTheme, useThemeColors } from "@/providers/ThemeProvider";
import { AgendaEvent, AgendaCategory } from "@/types";
import { format, isToday, isTomorrow } from "date-fns";
import { nl } from "date-fns/locale";

/**
 * Category icons mapping
 */
const CATEGORY_ICONS: Record<AgendaCategory | string, keyof typeof Ionicons.glyphMap> = {
  family_day: "sunny",
  family_weekend: "calendar",
  activity: "game-controller",
  meal: "restaurant",
  game: "trophy",
  ceremony: "ribbon",
  free_time: "cafe",
  custom: "ellipse",
};

/**
 * Default category colors
 */
const CATEGORY_COLORS: Record<AgendaCategory | string, string> = {
  family_day: "#F59E0B",
  family_weekend: "#7C3AED",
  activity: "#10B981",
  meal: "#EF4444",
  game: "#3B82F6",
  ceremony: "#EC4899",
  free_time: "#6366F1",
  custom: "#6B7280",
};

/**
 * Props for EventCard
 */
interface EventCardProps {
  event: AgendaEvent;
  onPress: () => void;
  compact?: boolean;
}

/**
 * Format time string for display (24h format)
 */
const formatEventTime = (startsAt: string, endsAt: string | null, allDay: boolean): string => {
  if (allDay) {
    return "Hele dag";
  }

  const start = format(new Date(startsAt), "HH:mm", { locale: nl });
  
  if (endsAt) {
    const end = format(new Date(endsAt), "HH:mm", { locale: nl });
    return `${start} - ${end}`;
  }

  return start;
};

/**
 * Get relative date label
 */
const getDateLabel = (date: Date): string | null => {
  if (isToday(date)) return "Vandaag";
  if (isTomorrow(date)) return "Morgen";
  return null;
};

/**
 * EventCard Component
 * Displays a single event in a card format
 */
export const EventCard = ({ event, onPress, compact = false }: EventCardProps) => {
  const { isDark } = useTheme();
  const colors = useThemeColors();

  const eventColor = event.color || CATEGORY_COLORS[event.category || "custom"];
  const eventIcon = event.icon || CATEGORY_ICONS[event.category || "custom"];
  const eventDate = new Date(event.starts_at);
  const dateLabel = getDateLabel(eventDate);

  // Check if this is a reveal event that's not yet revealed
  const isHiddenReveal = event.has_reveal && !event.is_revealed;

  return (
    <TouchableOpacity
      onPress={onPress}
      className={`rounded-xl overflow-hidden ${compact ? "p-3" : "p-4"}`}
      style={{ backgroundColor: colors.surface }}
      activeOpacity={0.7}
      accessibilityLabel={`Event: ${event.title}`}
      accessibilityRole="button"
    >
      <View className="flex-row items-start">
        {/* Color indicator bar */}
        <View
          className={`w-1 rounded-full mr-3 self-stretch ${compact ? "min-h-[40px]" : "min-h-[60px]"}`}
          style={{ backgroundColor: eventColor }}
        />

        {/* Content */}
        <View className="flex-1">
          {/* Header row */}
          <View className="flex-row items-center justify-between mb-1">
            <View className="flex-row items-center flex-1">
              <View
                className="w-7 h-7 rounded-full items-center justify-center mr-2"
                style={{ backgroundColor: `${eventColor}20` }}
              >
                <Ionicons name={eventIcon as any} size={14} color={eventColor} />
              </View>
              <Text
                className={`font-semibold flex-1 ${compact ? "text-sm" : ""}`}
                style={{ color: colors.text }}
                numberOfLines={1}
              >
                {isHiddenReveal ? "🔒 Verborgen evenement" : event.title}
              </Text>
            </View>

            {/* Visibility badge */}
            {event.visibility === "team_only" && (
              <View
                className="px-2 py-0.5 rounded-full ml-2"
                style={{ backgroundColor: colors.primaryLight }}
              >
                <Text className="text-xs font-medium" style={{ color: colors.primary }}>
                  Team
                </Text>
              </View>
            )}
            {event.visibility === "admin_only" && (
              <View
                className="px-2 py-0.5 rounded-full ml-2"
                style={{ backgroundColor: isDark ? "#78350F" : "#FEF3C7" }}
              >
                <Text
                  className="text-xs font-medium"
                  style={{ color: isDark ? "#FCD34D" : "#92400E" }}
                >
                  Admin
                </Text>
              </View>
            )}
          </View>

          {/* Time row */}
          <View className="flex-row items-center mb-1">
            <Ionicons name="time-outline" size={14} color={colors.textSecondary} />
            <Text className="text-sm ml-1.5" style={{ color: colors.textSecondary }}>
              {formatEventTime(event.starts_at, event.ends_at, event.all_day)}
            </Text>
            {dateLabel && (
              <View
                className="ml-2 px-2 py-0.5 rounded-full"
                style={{ backgroundColor: colors.primaryLight }}
              >
                <Text className="text-xs font-medium" style={{ color: colors.primary }}>
                  {dateLabel}
                </Text>
              </View>
            )}
          </View>

          {/* Location (if not hidden) */}
          {!isHiddenReveal && event.location && !compact && (
            <View className="flex-row items-center">
              <Ionicons name="location-outline" size={14} color={colors.textSecondary} />
              <Text
                className="text-sm ml-1.5 flex-1"
                style={{ color: colors.textSecondary }}
                numberOfLines={1}
              >
                {event.location}
              </Text>
            </View>
          )}

          {/* Description preview (if not compact and not hidden) */}
          {!isHiddenReveal && event.description && !compact && (
            <Text
              className="text-sm mt-2"
              style={{ color: colors.textTertiary }}
              numberOfLines={2}
            >
              {event.description}
            </Text>
          )}

          {/* Reveal indicator */}
          {event.has_reveal && (
            <View className="flex-row items-center mt-2">
              <Ionicons
                name={event.is_revealed ? "eye" : "lock-closed"}
                size={14}
                color={event.is_revealed ? colors.success : colors.warning}
              />
              <Text
                className="text-xs ml-1.5"
                style={{ color: event.is_revealed ? colors.success : colors.warning }}
              >
                {event.is_revealed
                  ? "Onthuld"
                  : event.reveal_type === "challenges"
                  ? "Voltooi uitdaging om te ontgrendelen"
                  : event.reveal_at
                  ? `Onthulling: ${format(new Date(event.reveal_at), "d MMM HH:mm", { locale: nl })}`
                  : "Nog niet onthuld"}
              </Text>
            </View>
          )}

          {/* Mandatory indicator */}
          {event.is_mandatory && (
            <View className="flex-row items-center mt-1">
              <Ionicons name="alert-circle" size={14} color={colors.warning} />
              <Text className="text-xs ml-1.5" style={{ color: colors.warning }}>
                Verplicht
              </Text>
            </View>
          )}
        </View>

        {/* Arrow */}
        <Ionicons
          name="chevron-forward"
          size={20}
          color={colors.textTertiary}
          style={{ marginLeft: 8 }}
        />
      </View>
    </TouchableOpacity>
  );
};

export default EventCard;

